import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { CloudArrowUpIcon, XMarkIcon, DocumentTextIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { useJobDescriptions } from '../contexts/JobDescriptionContext'
import { useResumes } from '../contexts/ResumeContext'
import { useToast } from '../contexts/ToastContext'
import { apiClient } from '../utils/api'
import { UploadedFile } from '../types'

interface FileUploadStatus {
  id: string
  file: File
  status: 'pending' | 'uploading' | 'success' | 'error'
  progress: number
  error?: string
}

export function ResumeUpload() {
  const { showSuccess, showError } = useToast();
  const [files, setFiles] = useState<FileUploadStatus[]>([])
  const [uploading, setUploading] = useState(false)
  const [selectedJdId, setSelectedJdId] = useState<string>('')
  const { jobDescriptions, loading: loadingJds } = useJobDescriptions()
  const { refreshResumes } = useResumes()

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map(file => ({
      id: Math.random().toString(36).substring(2, 11),
      file,
      status: 'pending' as const,
      progress: 0
    }))
    setFiles(prev => [...prev, ...newFiles])
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc']
    },
    multiple: true
  })

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id))
  }

  const uploadFiles = async () => {
    if (files.length === 0 || !selectedJdId) {
      showError('Please select files and a job description')
      return
    }

    setUploading(true)
    
    // Reset all files to pending status
    setFiles(prev => prev.map(f => ({ ...f, status: 'pending' as const, progress: 0, error: undefined })))

    try {
      // Upload files one by one to show individual progress
      for (let i = 0; i < files.length; i++) {
        const fileStatus = files[i]
        
        // Update status to uploading
        setFiles(prev => prev.map(f => 
          f.id === fileStatus.id 
            ? { ...f, status: 'uploading' as const, progress: 0 }
            : f
        ))

        try {
          // Upload single file
          await apiClient.uploadFiles('/resumes/upload', [fileStatus.file], { jdId: selectedJdId })
          
          // Mark as success
          setFiles(prev => prev.map(f => 
            f.id === fileStatus.id 
              ? { ...f, status: 'success' as const, progress: 100 }
              : f
          ))

        } catch (error) {
          // Mark as error
          setFiles(prev => prev.map(f => 
            f.id === fileStatus.id 
              ? { 
                  ...f, 
                  status: 'error' as const, 
                  progress: 0, 
                  error: error instanceof Error ? error.message : 'Upload failed' 
                }
              : f
          ))
        }
      }

      // Check if all files were successful
      const successfulUploads = files.filter(f => f.status === 'success').length
      const failedUploads = files.filter(f => f.status === 'error').length

      if (successfulUploads > 0) {
        showSuccess(`Successfully uploaded ${successfulUploads} resume(s)`)
        refreshResumes() // Refresh the resume table
      }

      if (failedUploads > 0) {
        showError(`${failedUploads} file(s) failed to upload`)
      }

      // Clear successful files after a delay
      setTimeout(() => {
        setFiles(prev => prev.filter(f => f.status !== 'success'))
      }, 3000)

    } catch (error) {
      console.error('Upload error:', error)
      showError(error instanceof Error ? error.message : 'Failed to upload resumes')
    } finally {
      setUploading(false)
    }
  }

  const getStatusIcon = (status: FileUploadStatus['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon className="h-4 w-4 text-success" />
      case 'error':
        return <ExclamationCircleIcon className="h-4 w-4 text-error" />
      case 'uploading':
        return <div className="loading loading-spinner loading-xs text-primary" />
      default:
        return <DocumentTextIcon className="h-4 w-4 text-base-content/60" />
    }
  }

  const getStatusText = (status: FileUploadStatus['status']) => {
    switch (status) {
      case 'success':
        return 'Uploaded'
      case 'error':
        return 'Failed'
      case 'uploading':
        return 'Uploading...'
      default:
        return 'Pending'
    }
  }

  return (
    <div className="space-y-4">
      {/* Job Description Selection */}
      <div>
        <label className="label">
          <span className="label-text font-medium">Select Job Description</span>
        </label>
        <select
          title="Select Job Description"
          className="select select-bordered w-full"
          value={selectedJdId}
          onChange={(e) => setSelectedJdId(e.target.value)}
          disabled={loadingJds}
        >
          <option value="">Choose a job description...</option>
          {jobDescriptions.map((jd) => (
            <option key={jd.id} value={jd.id}>
              {jd.title}
            </option>
          ))}
        </select>
        {loadingJds && (
          <div className="text-sm text-base-content/60 mt-1">
            Loading job descriptions...
          </div>
        )}
      </div>

      {/* File Upload Area */}
      <div>
        <label className="label">
          <span className="label-text font-medium">Upload Resumes</span>
        </label>
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-primary bg-primary/10'
              : 'border-base-300 hover:border-primary/50'
          }`}
        >
          <input {...getInputProps()} />
          <CloudArrowUpIcon className="mx-auto h-12 w-12 text-base-content/40 mb-4" />
          {isDragActive ? (
            <p className="text-primary font-medium">Drop the files here...</p>
          ) : (
            <div>
              <p className="text-base-content/70 mb-2">
                Drag & drop resume files here, or click to select files
              </p>
              <p className="text-sm text-base-content/50">
                Supports PDF, DOC, and DOCX files
              </p>
            </div>
          )}
        </div>
      </div>

      {/* File List with Status */}
      {files.length > 0 && (
        <div>
          <label className="label">
            <span className="label-text font-medium">Selected Files ({files.length})</span>
          </label>
          <div className="space-y-2">
            {files.map((fileStatus) => (
              <div
                key={fileStatus.id}
                className={`p-3 rounded-lg border ${
                  fileStatus.status === 'success' 
                    ? 'bg-success/10 border-success/20' 
                    : fileStatus.status === 'error'
                    ? 'bg-error/10 border-error/20'
                    : 'bg-base-200 border-base-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {getStatusIcon(fileStatus.status)}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">{fileStatus.file.name}</span>
                        <span className="text-xs text-base-content/50">
                          ({(fileStatus.file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      {fileStatus.error && (
                        <p className="text-xs text-error mt-1">{fileStatus.error}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-base-content/60">
                      {getStatusText(fileStatus.status)}
                    </span>
                    <button
                      title="Remove File"
                      onClick={() => removeFile(fileStatus.id)}
                      className="btn btn-ghost btn-xs text-error hover:text-error"
                      disabled={fileStatus.status === 'uploading'}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Button */}
      <button
        onClick={uploadFiles}
        disabled={uploading || files.length === 0 || !selectedJdId}
        className="btn btn-primary w-full"
      >
        {uploading ? (
          <div className="flex items-center gap-2">
            <span className="loading loading-spinner loading-sm"></span>
            <span>Uploading...</span>
          </div>
        ) : (
          `Upload ${files.length} Resume${files.length !== 1 ? 's' : ''}`
        )}
      </button>

      {/* Instructions */}
      <div className="text-sm text-base-content/60">
        <p>• Select a job description first</p>
        <p>• Upload PDF, DOC, or DOCX resume files</p>
        <p>• Files will be processed and analyzed against the selected job description</p>
      </div>
    </div>
  )
} 